'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import StarRating from '@/components/ui/StarRating';
import Link from 'next/link';

const GenreAffinityChart = dynamic(() => import('@/app/user-dashboard/components/GenreAffinityChart'), { ssr: false });
const ActivityChart = dynamic(() => import('@/app/user-dashboard/components/ActivityChart'), { ssr: false });

const statCards = [
{ id: 'stat-watched', label: 'Movies Watched', value: '247', change: '+12 this month', icon: 'EyeIcon', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
{ id: 'stat-watchlist', label: 'In Watchlist', value: '14', change: '3 added recently', icon: 'BookmarkIcon', color: 'text-accent', bg: 'bg-accent/10', border: 'border-accent/20' },
{ id: 'stat-reviews', label: 'Reviews Written', value: '38', change: '+2 this week', icon: 'StarIcon', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
{ id: 'stat-rank', label: 'Community Rank', value: '#142', change: 'Top 5% globally', icon: 'TrophyIcon', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' }];


const watchlistItems = [
{ id: 'wl-001', title: 'The Zone of Interest', year: 2023, genre: 'Drama', poster: "https://images.unsplash.com/photo-1633098471924-95a4104b1f58", posterAlt: 'Quiet countryside home with green trees', addedDate: '2 days ago', priority: 'high' },
{ id: 'wl-002', title: 'Priscilla', year: 2023, genre: 'Biography', poster: "https://images.unsplash.com/photo-1648451142763-6fb6244cb8a5", posterAlt: 'Vintage retro room with warm lighting', addedDate: '1 week ago', priority: 'medium' },
{ id: 'wl-003', title: 'Dream Scenario', year: 2023, genre: 'Comedy', poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1bef05f13-1772376525645.png", posterAlt: 'Film projector light in cinema', addedDate: '2 weeks ago', priority: 'low' },
{ id: 'wl-004', title: 'Society of the Snow', year: 2024, genre: 'Survival', poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1124275b0-1777831636217.png", posterAlt: 'Dramatic snowy mountain peaks', addedDate: '3 weeks ago', priority: 'high' },
{ id: 'wl-005', title: 'May December', year: 2023, genre: 'Drama', poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6ea8eef-1784119722063.png", posterAlt: 'Classic cinema projector beam', addedDate: '1 month ago', priority: 'medium' },
{ id: 'wl-006', title: 'Bob Marley: One Love', year: 2024, genre: 'Biography', poster: "https://images.unsplash.com/photo-1668155926046-1ca33310f73b", posterAlt: 'Concert stage with colorful lights', addedDate: '1 month ago', priority: 'low' }];


const watchedHistory = [
{ id: 'wh-001', title: 'Oppenheimer', year: 2023, genre: 'Drama', userRating: 5, watchedDate: 'Jul 22, 2026', poster: "https://img.rocket.new/generatedImages/rocket_gen_img_1b011a2e2-1785350644295.png", posterAlt: 'Abstract nuclear light patterns' },
{ id: 'wh-002', title: 'Dune: Part Two', year: 2024, genre: 'Sci-Fi', userRating: 5, watchedDate: 'Jul 15, 2026', poster: "https://images.unsplash.com/photo-1711385026698-b051ff9a2e3e", posterAlt: 'Desert landscape with dramatic sky' },
{ id: 'wh-003', title: 'Poor Things', year: 2023, genre: 'Fantasy', userRating: 4, watchedDate: 'Jul 8, 2026', poster: "https://images.unsplash.com/photo-1724858611896-cee5c3eb795f", posterAlt: 'Surreal colorful mountain landscape' },
{ id: 'wh-004', title: 'Past Lives', year: 2023, genre: 'Romance', userRating: 5, watchedDate: 'Jun 30, 2026', poster: "https://images.unsplash.com/photo-1524519562116-3d037ec85d3b", posterAlt: 'City lights at night with bokeh' },
{ id: 'wh-005', title: 'Spider-Man: Across the Spider-Verse', year: 2023, genre: 'Animation', userRating: 5, watchedDate: 'Jun 22, 2026', poster: "https://img.rocket.new/generatedImages/rocket_gen_img_125897db6-1768039136141.png", posterAlt: 'Colorful cosmic space scene' },
{ id: 'wh-006', title: 'The Holdovers', year: 2023, genre: 'Comedy', userRating: 4, watchedDate: 'Jun 14, 2026', poster: "https://images.unsplash.com/photo-1546924645-c12aa2b79285", posterAlt: 'Snow-covered campus buildings' },
{ id: 'wh-007', title: 'Saltburn', year: 2023, genre: 'Thriller', userRating: 3, watchedDate: 'Jun 5, 2026', poster: "https://images.unsplash.com/photo-1546270828-376a33ded32d", posterAlt: 'Dark gothic mansion with green grounds' },
{ id: 'wh-008', title: 'Killers of the Flower Moon', year: 2023, genre: 'History', userRating: 4, watchedDate: 'May 28, 2026', poster: "https://images.unsplash.com/photo-1730828550968-3bd9456b58c2", posterAlt: 'Vast American plains at golden hour' }];


const activityFeed = [
{ id: 'act-001', type: 'review', text: 'Wrote a review for Oppenheimer', time: '2 hours ago', icon: 'StarIcon', color: 'text-accent' },
{ id: 'act-002', type: 'watched', text: 'Marked Dune: Part Two as watched', time: '3 days ago', icon: 'EyeIcon', color: 'text-green-400' },
{ id: 'act-003', type: 'watchlist', text: 'Added Society of the Snow to watchlist', time: '1 week ago', icon: 'BookmarkIcon', color: 'text-purple-400' },
{ id: 'act-004', type: 'discussion', text: 'Replied to "Best films of 2023" thread', time: '1 week ago', icon: 'ChatBubbleLeftIcon', color: 'text-blue-400' },
{ id: 'act-005', type: 'submission', text: 'Submitted "The Substance" for review', time: '2 weeks ago', icon: 'PlusCircleIcon', color: 'text-primary' },
{ id: 'act-006', type: 'review', text: 'Wrote a review for Past Lives', time: '2 weeks ago', icon: 'StarIcon', color: 'text-accent' }];


const badges = [
{ id: 'badge-centurion', label: 'Centurion', desc: '100+ movies watched', icon: '🎬', earned: true },
{ id: 'badge-critic', label: 'Film Critic', desc: '25+ reviews written', icon: '✍️', earned: true },
{ id: 'badge-contributor', label: 'Contributor', desc: 'Submitted 5+ movies', icon: '🎥', earned: true },
{ id: 'badge-marathoner', label: 'Marathoner', desc: '10 movies in one week', icon: '🏃', earned: false },
{ id: 'badge-legend', label: 'Legend', desc: '500+ movies watched', icon: '👑', earned: false }];


export default function DashboardOverview() {
  const [watchlistView, setWatchlistView] = useState<'grid' | 'list'>('grid');
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleRemoveFromWatchlist = (id: string) => {
    setRemovingId(id);
    // Backend integration: DELETE /api/watchlist/:id
    setTimeout(() => setRemovingId(null), 600);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-800 text-foreground">My Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Welcome back, Marcus. Here&apos;s your cinema journey.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="ClockIcon" size={12} />
          Last active: 2 hours ago
        </div>
      </div>

      {/* Stat cards — 4 cards → 2x2 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((card) =>
        <div key={card.id} className={`p-4 rounded-2xl bg-card border ${card.border} card-hover`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2 rounded-xl ${card.bg}`}>
                <Icon name={card.icon as Parameters<typeof Icon>[0]['name']} size={18} className={card.color} />
              </div>
            </div>
            <div className={`text-3xl font-800 font-mono-nums ${card.color} mb-0.5`}>{card.value}</div>
            <div className="text-xs font-600 text-foreground">{card.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{card.change}</div>
          </div>
        )}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="p-5 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 bg-primary rounded-full" />
            <h3 className="text-sm font-700 text-foreground">Watch Activity — Last 12 Weeks</h3>
          </div>
          <ActivityChart />
        </div>
        <div className="p-5 rounded-2xl bg-card border border-border">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-4 bg-gold rounded-full" />
            <h3 className="text-sm font-700 text-foreground">Genre Affinity</h3>
          </div>
          <GenreAffinityChart />
        </div>
      </div>

      {/* Badges */}
      <div className="p-5 rounded-2xl bg-card border border-border">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-1 h-4 bg-gold rounded-full" />
          <h3 className="text-sm font-700 text-foreground">Achievements</h3>
          <span className="text-xs text-muted-foreground ml-1">{badges.filter((b) => b.earned).length}/{badges.length} earned</span>
        </div>
        <div className="flex flex-wrap gap-3">
          {badges.map((badge) =>
          <div
            key={badge.id}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all duration-150 ${
            badge.earned ?
            'bg-gold/10 border-gold/25 gold-glow' : 'bg-muted/30 border-border opacity-50'}`
            }>
            
              <span className="text-lg">{badge.icon}</span>
              <div>
                <p className={`text-xs font-700 ${badge.earned ? 'text-gold' : 'text-muted-foreground'}`}>{badge.label}</p>
                <p className="text-xs text-muted-foreground">{badge.desc}</p>
              </div>
              {badge.earned && <Icon name="CheckCircleIcon" variant="solid" size={14} className="text-gold ml-1" />}
            </div>
          )}
        </div>
      </div>

      {/* Watchlist */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-1 h-5 bg-accent rounded-full" />
            <h2 className="text-base font-700 text-foreground">My Watchlist</h2>
            <span className="text-sm text-muted-foreground">({watchlistItems.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button onClick={() => setWatchlistView('grid')} className={`p-2 transition-colors ${watchlistView === 'grid' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`} aria-label="Grid view">
                <Icon name="Squares2X2Icon" size={14} />
              </button>
              <button onClick={() => setWatchlistView('list')} className={`p-2 transition-colors ${watchlistView === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`} aria-label="List view">
                <Icon name="ListBulletIcon" size={14} />
              </button>
            </div>
          </div>
        </div>

        {watchlistView === 'grid' ?
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
            {watchlistItems.map((item) =>
          <div
            key={item.id}
            className={`relative rounded-xl overflow-hidden group transition-all duration-300 ${removingId === item.id ? 'opacity-0 scale-95' : 'opacity-100'}`}>
            
                <div className="relative h-40 bg-muted">
                  <AppImage src={item.poster} alt={item.posterAlt} fill className="object-cover" sizes="160px" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1">
                    <button
                  onClick={() => handleRemoveFromWatchlist(item.id)}
                  className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg bg-primary/80 text-white text-xs font-600">
                  
                      <Icon name="TrashIcon" size={10} />
                      Remove
                    </button>
                    <button className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg bg-green-600/80 text-white text-xs font-600">
                      <Icon name="CheckIcon" size={10} />
                      Watched
                    </button>
                  </div>
                </div>
                <div className="p-2 bg-card">
                  <p className="text-xs font-600 text-foreground line-clamp-2 leading-snug">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.year} · {item.genre}</p>
                </div>
              </div>
          )}
          </div> :

        <div className="space-y-2">
            {watchlistItems.map((item) =>
          <div key={item.id} className={`flex items-center gap-3 p-3 rounded-xl bg-card border border-border hover:border-border/80 transition-all duration-150 group ${removingId === item.id ? 'opacity-0' : 'opacity-100'}`}>
                <div className="relative w-10 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                  <AppImage src={item.poster} alt={item.posterAlt} fill className="object-cover" sizes="40px" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-600 text-foreground truncate">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.year} · {item.genre}</p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">Added {item.addedDate}</p>
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="btn-ghost p-1.5 rounded-lg" aria-label="Mark as watched">
                    <Icon name="CheckCircleIcon" size={14} className="text-green-400" />
                  </button>
                  <button onClick={() => handleRemoveFromWatchlist(item.id)} className="btn-ghost p-1.5 rounded-lg" aria-label="Remove from watchlist">
                    <Icon name="TrashIcon" size={14} className="text-primary" />
                  </button>
                </div>
              </div>
          )}
          </div>
        }
      </div>

      {/* Watched history */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <span className="w-1 h-5 bg-green-500 rounded-full" />
          <h2 className="text-base font-700 text-foreground">Watch History</h2>
          <span className="text-sm text-muted-foreground">(247 total)</span>
        </div>
        <div className="rounded-2xl border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Film</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Genre</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Your Rating</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider hidden md:table-cell">Watched</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {watchedHistory.map((item) =>
              <tr key={item.id} className="thread-row group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="relative w-8 h-11 rounded overflow-hidden flex-shrink-0 bg-muted">
                        <AppImage src={item.poster} alt={item.posterAlt} fill className="object-cover" sizes="32px" />
                      </div>
                      <div>
                        <Link href="/movie-detail" className="text-sm font-600 text-foreground hover:text-accent transition-colors">{item.title}</Link>
                        <p className="text-xs text-muted-foreground">{item.year}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-xs text-muted-foreground">{item.genre}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StarRating value={item.userRating} size={12} />
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-xs text-muted-foreground">{item.watchedDate}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="btn-ghost p-1.5 rounded-lg" aria-label="Edit rating">
                        <Icon name="PencilIcon" size={12} className="text-muted-foreground" />
                      </button>
                      <button className="btn-ghost p-1.5 rounded-lg" aria-label="Remove from history">
                        <Icon name="TrashIcon" size={12} className="text-muted-foreground" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-border flex items-center justify-between bg-muted/20">
            <p className="text-xs text-muted-foreground">Showing 8 of 247 entries</p>
            <button className="text-xs text-accent hover:underline font-600">View all history</button>
          </div>
        </div>
      </div>

      {/* Activity feed */}
      <div className="p-5 rounded-2xl bg-card border border-border">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-1 h-4 bg-blue-400 rounded-full" />
          <h3 className="text-sm font-700 text-foreground">Recent Activity</h3>
        </div>
        <div className="space-y-3">
          {activityFeed.map((item) =>
          <div key={item.id} className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={13} className={item.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">{item.text}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.time}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

}